'use client'
import { useEffect } from 'react'

import { GameId } from '@common'
import cx from 'classnames'
import Image from 'next/image'
import Link from 'next/link'
import { useRouter } from 'next/router'
import { useTranslations } from 'next-intl'

import RoyaleHeaderFlag from '@/assets/images/storefronts/clashroyale/royale-header-flag.png'
import { useGameImages } from '@/components/games/images'
import { useAppSelector } from '@/utils/useAppSelector'
import { useBreakpoints } from '@/utils/useBreakpoints'

import { CategoryLinks } from './CategoryLinks'
import { Menu } from './menu/Menu'
import { useMenu } from './menu/menu-provider'
import { MenuIcon, SIcon, SupportIcon } from '../../../../components/Icons'
import { useIsLoggedIn } from '../../../common/useIsLoggedIn'
import { UserAvatarImage } from '../../../user/avatar/UserAvatarImage'
import { CartButton } from '../cart/CartButton'
import { GAME_THEMES } from '../constants'

import styles from './header.module.scss'

type Props = {
  game: GameId
}
export function Header({ game }: Props) {
  const loginStatus = useAppSelector((state) => state.user.loginStatus)
  const { pathname } = useRouter()
  const { gameImage } = useGameImages(game)
  const isLoggedIn = useIsLoggedIn()
  const { setIsOpen } = useMenu()

  useEffect(() => {
    document.documentElement.style.setProperty('--game-theme', GAME_THEMES[game])

    return () => {
      document.documentElement.style.removeProperty('--game-theme')
    }
  }, [game])

  const t = useTranslations()
  const infoLinks = [
    {
      icon: SupportIcon,
      label: t('help_support_title'),
      href: t('help_url'),
      target: '_blank',
    },
    {
      icon: SIcon,
      label: t('our_other_game_stores'),
      href: '/',
    },
  ]

  const categoryLinks = [
    { label: t('specials_title'), href: '#storespecials' },
    { label: t('passes_title'), href: '#passes' },
    { label: t('offers_title'), href: '#offers' },
  ]

  const from = useBreakpoints().from
  const isDesktop = from('laptop-small')

  const isProductPage = pathname.includes('/product')
  const HeaderLogo =
    game == 'squadbusters' ? (
      <Image src={gameImage('headerLogo')} alt={game} fill quality={100} sizes="200px" />
    ) : isDesktop ? (
      <Image src={gameImage('headerLogo')} alt={game} fill quality={100} sizes="200px" />
    ) : game === 'clashroyale' ? (
      <Image src={RoyaleHeaderFlag} alt={game} fill quality={100} sizes="200px" />
    ) : (
      <Image src={gameImage('badge')} alt={game} fill quality={100} sizes="100px" />
    )

  const headerBgStyle = game !== 'clashroyale' ? { backgroundImage: `url(/images/${game}/topbar-bg.png)` } : {}

  return (
    <>
      <div className={styles.menuContainer}>
        <Menu
          infoLinks={infoLinks}
          loginStatus={loginStatus}
          categoryLinks={categoryLinks}
          isDesktop={isDesktop}
          isProductPage={isProductPage}
        />
      </div>

      <div className={styles.wrapper}>
        <header className={cx(styles.header, styles[game])}>
          <div className={styles.headerBg} style={headerBgStyle} />

          <div className={styles.content}>
            <Link href={`/${game}`} tabIndex={0} className={cx(styles.logo, styles[game])}>
              {HeaderLogo}
            </Link>
            {isDesktop && !isProductPage && <CategoryLinks categories={categoryLinks} />}

            <div className={styles.navButtons}>
              <CartButton className={styles.cart} />

              <button
                className={cx(styles.menuButton, styles[game], { [styles.isLoggedIn]: isLoggedIn })}
                onClick={() => setIsOpen(true)}
                data-cy="menu-button"
              >
                {isLoggedIn && (
                  <UserAvatarImage layout="fill" quality={100} objectFit="contain" className={styles.menuAvatar} />
                )}
                <div className={styles.menuIcon}>
                  <MenuIcon />
                </div>
              </button>
            </div>
          </div>
        </header>
      </div>
    </>
  )
}
